/* Brilinton Sticky Navigation Component
   Fast path: one rAF-throttled controller, no polling, no hardcoded header pixels. */
(function () {
  if (window.BrilintonStickyNavigation) return;

  const SELECTOR = '[data-sticky-nav]';
  const HEADER_SELECTOR = '.shopify-section-header-sticky, .section-header, sticky-header, .shopify-section-group-header-group';
  const instances = new Set();
  let ticking = false;
  let refreshTicking = false;

  function visible(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  function getHeaderBottom() {
    const rootStyle = getComputedStyle(document.documentElement);
    const storedOffset = parseFloat(rootStyle.getPropertyValue('--site-sticky-offset'));
    if (!Number.isNaN(storedOffset) && storedOffset > 0) return Math.floor(storedOffset);

    const desktopOffset = parseFloat(rootStyle.getPropertyValue('--header-bottom-position-desktop'));
    if (!Number.isNaN(desktopOffset) && desktopOffset > 0) return Math.floor(desktopOffset);

    const headerHeight = parseFloat(rootStyle.getPropertyValue('--header-height'));
    if (!Number.isNaN(headerHeight) && headerHeight > 0) return Math.floor(headerHeight);

    const headers = Array.from(document.querySelectorAll(HEADER_SELECTOR)).filter(visible);
    let height = 0;

    for (const header of headers) {
      if (header.classList.contains('shopify-section-header-hidden')) continue;
      const rect = header.getBoundingClientRect();
      if (rect.height > height) height = rect.height;
    }

    return Math.max(0, Math.floor(height));
  }

  class StickyNav {
    constructor(nav) {
      if (!nav || nav.dataset.stickyNavBound === 'true') return;
      this.nav = nav;
      this.nav.dataset.stickyNavBound = 'true';
      this.type = nav.dataset.stickyNavType || 'section-tabs';
      this.links = Array.from(nav.querySelectorAll('[data-target], .sticky-nav__link[href]'));
      this.progress = nav.querySelector('.sticky-nav__progress-fill');
      this.spacer = this.findOrCreateSpacer();
      this.naturalTop = 0;
      this.navHeight = 0;
      this.fixed = false;
      this.studioSuspended = false;
      this.bind();
      this.refresh();
    }

    findOrCreateSpacer() {
      let spacer = this.nav.nextElementSibling;
      if (spacer && spacer.classList.contains('sticky-nav__spacer')) {
        spacer.classList.add('sticky-nav__spacer');
        return spacer;
      }
      spacer = document.createElement('div');
      spacer.className = 'sticky-nav__spacer';
      spacer.setAttribute('aria-hidden', 'true');
      this.nav.insertAdjacentElement('afterend', spacer);
      return spacer;
    }

    isStudioMobileOpen() {
      return document.documentElement.classList.contains('studio-mobile-open') ||
        document.body.classList.contains('studio-mobile-open') ||
        document.documentElement.classList.contains('studio-page-mobile-open') ||
        document.body.classList.contains('studio-page-mobile-open');
    }

    bind() {
      if (this.type === 'section-tabs') {
        this.links.forEach(link => {
          if (!link.dataset.target || link.dataset.stickyNavClickBound === 'true') return;
          link.dataset.stickyNavClickBound = 'true';
          link.addEventListener('click', event => {
            const target = document.querySelector(link.dataset.target);
            if (!target) return;
            event.preventDefault();
            const offset = getHeaderBottom() + this.nav.offsetHeight + 10;
            const top = target.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: 'smooth' });
          });
        });
      }

      if (this.type === 'center' || this.type === 'help-center') {
        this.bindCenterDrawer();
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this.nav);
      }
    }

    bindCenterDrawer() {
      const openButtons = Array.from(this.nav.querySelectorAll('[data-center-nav-open]'));
      const drawerId = openButtons[0]?.getAttribute('aria-controls') || 'centerMobileDrawer';
      const drawer = document.getElementById(drawerId);
      const closeButtons = drawer ? Array.from(document.querySelectorAll('[data-center-nav-close]')) : [];
      if (!drawer || !openButtons.length) return;

      let lockedScrollY = 0;

      const lockPageScroll = () => {
        document.documentElement.classList.add('center-drawer-open');
        if (window.BrilintonSurfaceManager) {
          window.BrilintonSurfaceManager.register('center-drawer', {
            root: () => drawer,
            close: () => setOpen(false),
          });
          window.BrilintonSurfaceManager.open('center-drawer', {
            trigger: document.activeElement,
            lockMode: 'fixed',
          });
          return;
        }
        lockedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + lockedScrollY + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      };

      const unlockPageScroll = () => {
        document.documentElement.classList.remove('center-drawer-open');
        if (window.BrilintonSurfaceManager) {
          window.BrilintonSurfaceManager.close('center-drawer', { restoreFocus: true });
          return;
        }
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, lockedScrollY);
      };

      const setOpen = (open) => {
        const currentlyOpen = drawer.classList.contains('active') || drawer.classList.contains('is-open');
        drawer.classList.toggle('active', open);
        drawer.classList.toggle('is-open', open);
        drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
        if (open && !currentlyOpen) lockPageScroll();
        if (!open && currentlyOpen) unlockPageScroll();
        openButtons.forEach(button => button.setAttribute('aria-expanded', open ? 'true' : 'false'));
      };

      drawer.setAttribute('aria-hidden', drawer.classList.contains('active') || drawer.classList.contains('is-open') ? 'false' : 'true');

      openButtons.forEach(button => {
        if (button.dataset.centerNavBound === 'true') return;
        button.dataset.centerNavBound = 'true';
        button.addEventListener('click', event => {
          event.preventDefault();
          const isOpen = drawer.classList.contains('active') || drawer.classList.contains('is-open');
          setOpen(!isOpen);
        });
      });

      closeButtons.forEach(button => {
        if (button.dataset.centerNavBound === 'true') return;
        button.dataset.centerNavBound = 'true';
        button.addEventListener('click', event => {
          event.preventDefault();
          setOpen(false);
        });
      });

      drawer.querySelectorAll('a[href]').forEach(link => {
        if (link.dataset.centerNavCloseBound === 'true') return;
        link.dataset.centerNavCloseBound = 'true';
        link.addEventListener('click', () => setOpen(false));
      });

      if (drawer.dataset.escapeBound !== 'true') {
        drawer.dataset.escapeBound = 'true';
        document.addEventListener('keydown', event => {
          if (event.key === 'Escape' && (drawer.classList.contains('active') || drawer.classList.contains('is-open'))) {
            setOpen(false);
          }
        });
      }
    }

    clearFixed(skipRefresh) {
      this.fixed = false;
      this.nav.classList.remove('is-fixed', 'is-stuck', 'sticky-nav--is-fixed');
      this.nav.style.position = '';
      this.nav.style.top = '';
      this.nav.style.left = '';
      this.nav.style.right = '';
      this.nav.style.width = '';
      this.nav.style.zIndex = '';
      this.spacer.style.display = 'none';
      this.spacer.style.height = '0px';
      this.spacer.classList.remove('is-active');
      if (!skipRefresh) this.measure();
    }

    applyFixed(headerBottom) {
      this.fixed = true;
      this.nav.classList.add('is-fixed', 'is-stuck', 'sticky-nav--is-fixed');
      this.nav.style.position = 'fixed';
      this.nav.style.top = headerBottom + 'px';
      this.nav.style.left = '0';
      this.nav.style.right = '0';
      this.nav.style.width = '100%';
      this.nav.style.zIndex = getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-nav-z-index').trim() || '45';
      this.spacer.style.display = 'block';
      this.spacer.style.height = this.nav.offsetHeight + 'px';
      this.spacer.classList.add('is-active');
    }

    updateTabVisibility() {
      if (this.type !== 'section-tabs') return;
      let anyVisible = false;
      this.links.forEach(link => {
        const target = link.dataset.target ? document.querySelector(link.dataset.target) : null;
        const item = link.closest('li');
        if (!item) return;
        let isVisible = false;
        if (target) {
          const style = window.getComputedStyle(target);
          isVisible = !(style.display === 'none' || style.visibility === 'hidden' || target.offsetParent === null);
        }
        item.style.display = isVisible ? '' : 'none';
        if (isVisible) anyVisible = true;
      });
      this.nav.style.display = anyVisible ? '' : 'none';
    }

    updateProgress() {
      if (!this.progress) return;
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0;
      this.progress.style.width = Math.max(0, Math.min(100, pct)) + '%';
    }

    measure() {
      const wasFixed = this.fixed;
      if (wasFixed) this.clearFixed(true);
      this.navHeight = this.nav.offsetHeight || 0;
      this.naturalTop = this.nav.getBoundingClientRect().top + window.scrollY;
      if (wasFixed) this.applyFixed(getHeaderBottom());
    }

    refresh() {
      this.updateTabVisibility();
      this.measure();
      this.update();
    }

    update() {
      if (!visible(this.nav)) return;
      this.updateProgress();

      if (this.isStudioMobileOpen()) {
        this.studioSuspended = true;
        this.clearFixed(true);
        return;
      }

      if (this.studioSuspended) {
        this.studioSuspended = false;
        this.refresh();
        return;
      }

      const headerBottom = getHeaderBottom();
      const shouldFix = window.scrollY + headerBottom >= this.naturalTop;
      if (shouldFix) {
        this.applyFixed(headerBottom);
      } else if (this.fixed) {
        this.clearFixed();
      }
    }

    disconnect() {
      this.resizeObserver?.disconnect();
    }
  }

  function init(root = document) {
    root.querySelectorAll(SELECTOR).forEach(nav => {
      nav.classList.add('sticky-nav');
      if (nav.dataset.stickyNavType === 'section-tabs') nav.classList.add('sticky-nav--section-tabs');
      if (nav.dataset.stickyNavType === 'center') nav.classList.add('sticky-nav--center');
      if (nav.dataset.stickyNavType === 'help-center') nav.classList.add('sticky-nav--center');
      const instance = new StickyNav(nav);
      if (instance.nav) instances.add(instance);
    });
    scheduleRefresh();
  }

  function scheduleUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      instances.forEach(instance => instance.update());
      ticking = false;
    });
  }

  function scheduleRefresh() {
    if (refreshTicking) return;
    refreshTicking = true;
    window.requestAnimationFrame(() => {
      refreshTicking = false;
      instances.forEach(instance => {
        if (!instance.nav || !instance.nav.isConnected) {
          instance.disconnect?.();
          instances.delete(instance);
          return;
        }
        instance.refresh();
      });
    });
  }

  window.BrilintonStickyNavigation = { init, refresh: scheduleRefresh, update: scheduleUpdate };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleRefresh, { passive: true });
  window.addEventListener('orientationchange', scheduleRefresh, { passive: true });
  window.addEventListener('brilinton:studio-mobile-lock', scheduleRefresh);
  window.addEventListener('brilinton:studio-mobile-unlock', scheduleRefresh);
  window.addEventListener('brilinton:layout-refresh', scheduleRefresh);

  if (window.MutationObserver) {
    const observer = new MutationObserver(() => scheduleRefresh());
    document.addEventListener('DOMContentLoaded', () => {
      const header = document.querySelector(HEADER_SELECTOR);
      if (header) observer.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
    });
  }
})();
